import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingContext, M_Fieldset, M_Button, BufferService } from 'src/M_Components'
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material'
import {
    ExpandMore,
    Delete,
    Edit,
    FolderSpecial,
    BookmarkAdd,
    Star,
    Refresh,
    StarOutline
} from '@mui/icons-material'

export const FavoritesSettings = () => {
    const ctx = useContext(LoadingContext)
    const { t } = useTranslation()

    const [favorites, setFavorites] = useState([])
    const [expandedId, setExpandedId] = useState(null)
    const [editDialog, setEditDialog] = useState({ open: false, favorite: null })
    const [deleteDialog, setDeleteDialog] = useState({ open: false, favorite: null })
    const [editName, setEditName] = useState('')

    const favoriteService = new BufferService('Favorites')
    const userPreferencesService = new BufferService('userpreferences/favorites')

    useEffect(() => {
        loadFavorites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadFavorites = async () => {
        ctx.setIsLoad(true)
        try {
            const userId = localStorage.getItem('userId')
            const data = await userPreferencesService.getId(userId)
            if (data && !data.name) {
                const items = Array.isArray(data) ? data : data.Items || []
                setFavorites(items)
            }
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorLoadingFavorites'))
        } finally {
            ctx.setIsLoad(false)
        }
    }

    const handleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id)
    }

    const handleOpenEditDialog = (favorite, event) => {
        event.stopPropagation()
        setEditName(favorite.Description)
        setEditDialog({ open: true, favorite })
    }

    const handleCloseEditDialog = () => {
        setEditDialog({ open: false, favorite: null })
        setEditName('')
    }

    const handleSaveEdit = async () => {
        if (!editName.trim()) {
            ctx.toastMessages('error', '', t('FavoriteNameRequired'))
            return
        }

        try {
            const updatedFavorite = {
                ...editDialog.favorite,
                Description: editName
            }

            const response = await favoriteService.update(editDialog.favorite.Id, updatedFavorite)

            if (response && !response.name) {
                ctx.toastMessages('success', '', t('FavoriteUpdated'))
                handleCloseEditDialog()
                loadFavorites()
            } else {
                ctx.toastMessages('error', '', response?.message || t('ErrorUpdatingFavorite'))
            }
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorUpdatingFavorite'))
        }
    }

    const handleOpenDeleteDialog = (favorite, event) => {
        event.stopPropagation()
        setDeleteDialog({ open: true, favorite })
    }

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({ open: false, favorite: null })
    }

    const handleDelete = async () => {
        try {
            await favoriteService.delete(deleteDialog.favorite.Id)
            ctx.toastMessages('success', '', t('FavoriteDeleted'))
            handleCloseDeleteDialog()
            loadFavorites()
        } catch (error) {
            ctx.toastMessages('error', '', t('ErrorDeletingFavorite'))
        }
    }

    const parseMetadata = (metadata) => {
        try {
            return typeof metadata === 'string' ? JSON.parse(metadata) : metadata
        } catch {
            return null
        }
    }

    const renderMetadataContent = (metadata) => {
        if (!metadata) return <span style={{ color: '#999' }}>{t('NoData')}</span>

        if (metadata.type === 'field') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', minWidth: '100px' }}>{t('Field')}:</span>
                        <span>{metadata.fieldName}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', minWidth: '100px' }}>{t('Value')}:</span>
                        <span style={{ color: 'var(--color-green-dark)' }}>
                            {metadata.displayValue || metadata.fieldValue}
                        </span>
                    </div>
                    {metadata.groupName && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold', minWidth: '100px' }}>{t('Group')}:</span>
                            <span>{t(metadata.groupName)}</span>
                        </div>
                    )}
                </div>
            )
        }

        if (metadata.type === 'group') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{
                        backgroundColor: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                    }}>
                        {Object.entries(metadata.fields || {}).map(([fieldName, fieldData]) => (
                            <div key={fieldName} style={{
                                display: 'flex',
                                gap: '8px',
                                marginBottom: '6px',
                                alignItems: 'flex-start'
                            }}>
                                <span style={{
                                    color: '#666',
                                    minWidth: '140px',
                                    fontSize: '13px'
                                }}>
                                    {t(fieldData.label || fieldName)}:
                                </span>
                                <span style={{
                                    fontWeight: '500',
                                    wordBreak: 'break-all',
                                    color: 'var(--color-green-dark)'
                                }}>
                                    {fieldData.displayValue || fieldData.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )
        }

        return <pre style={{ fontSize: '12px', margin: 0 }}>{JSON.stringify(metadata, null, 2)}</pre>
    }

    const groupFavoritesByEntity = () => {
        const grouped = {}
        favorites.forEach(fav => {
            const entity = fav.EntityName || 'Other'
            if (!grouped[entity]) {
                grouped[entity] = []
            }
            grouped[entity].push(fav)
        })
        return grouped
    }

    const groupedFavorites = groupFavoritesByEntity()

    return (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '10px'
            }}>
                <M_Button
                    icon={<Refresh />}
                    label={t('Refresh')}
                    onClick={loadFavorites}
                    normal
                    hoverEffect
                />
            </div>

            <M_Fieldset
                labelInside
                label={t('Favorites')}
                elements={
                    <div style={{ padding: '8px' }}>
                        {favorites.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#999'
                            }}>
                                <Star sx={{ fontSize: 48, color: '#ddd', marginBottom: '16px' }} />
                                <div style={{ fontSize: '20px', marginBottom: '5px' }}>{t('NoFavoritesFound')}</div>
                                <div style={{ fontSize: '15px', marginTop: '5px' }}>{t('RightClickToSaveFavorite')}</div>
                            </div>
                        ) : (
                            Object.entries(groupedFavorites).map(([entityName, entityFavorites]) => (
                                <div key={entityName} style={{ marginBottom: '16px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '8px',
                                        paddingBottom: '8px',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}>
                                        <Star sx={{ color: 'var(--color-yellow)' }} />
                                        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                            {t(entityName)}
                                        </span>
                                        <Chip
                                            label={entityFavorites.length}
                                            size="small"
                                            sx={{ backgroundColor: '#e3f2fd' }}
                                        />
                                    </div>

                                    {entityFavorites.map((favorite) => {
                                        const metadata = parseMetadata(favorite.Metadata)
                                        const isGroup = metadata?.type === 'group'

                                        return (
                                            <Accordion
                                                key={favorite.Id}
                                                expanded={expandedId === favorite.Id}
                                                onChange={() => handleExpand(favorite.Id)}
                                                sx={{
                                                    marginBottom: '8px',
                                                    border: `1px solid ${isGroup ? '#90caf9' : '#81c784'}`,
                                                    borderRadius: '8px !important',
                                                    '&:before': { display: 'none' },
                                                    boxShadow: 'none'
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore />}
                                                    sx={{
                                                        backgroundColor: isGroup ? '#e3f2fd' : '#e8f5e9',
                                                        borderRadius: expandedId === favorite.Id
                                                            ? '8px 8px 0 0'
                                                            : '8px',
                                                        '&:hover': {
                                                            backgroundColor: isGroup ? '#bbdefb' : '#c8e6c9'
                                                        }
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                        paddingRight: '8px'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                                            {isGroup ? (
                                                                <FolderSpecial sx={{ color: 'var(--color-blue)', fontSize: '18px' }} />
                                                            ) : (
                                                                <StarOutline sx={{ color: 'var(--color-green)', fontSize: '18px' }} />
                                                            )}
                                                            <span style={{ fontWeight: 'bold' }}>
                                                                {favorite.Description}
                                                            </span>
                                                            <strong>
                                                                <span style={{
                                                                    fontSize: '10px',
                                                                    color: '#666',
                                                                    backgroundColor: '#e0e0e0',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px'
                                                                }}>
                                                                    {t(favorite.Category)}
                                                                </span>
                                                            </strong>
                                                        </div>

                                                        <div style={{ display: 'flex', gap: '4px' }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => handleOpenEditDialog(favorite, e)}
                                                                sx={{
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(33, 150, 243, 0.1)'
                                                                    }
                                                                }}
                                                            >
                                                                <Edit sx={{ fontSize: 18, color: 'var(--color-blue)' }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => handleOpenDeleteDialog(favorite, e)}
                                                                sx={{
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                                                    }
                                                                }}
                                                            >
                                                                <Delete sx={{ fontSize: 18, color: 'var(--color-red)' }} />
                                                            </IconButton>
                                                        </div>
                                                    </div>
                                                </AccordionSummary>

                                                <AccordionDetails sx={{ padding: '16px' }}>
                                                    {renderMetadataContent(metadata)}
                                                </AccordionDetails>
                                            </Accordion>
                                        )
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                }
            />

            {/* Edit Dialog */}
            <Dialog
                open={editDialog.open}
                onClose={handleCloseEditDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Edit sx={{ color: 'var(--color-blue)' }} />
                    {t('EditFavorite')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('Name')}
                        fullWidth
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        sx={{ marginTop: '16px' }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: '16px' }}>
                    <M_Button
                        label={t('Cancel')}
                        onClick={handleCloseEditDialog}
                        normal
                        hoverEffect
                    />
                    <M_Button
                        label={t('Save')}
                        onClick={handleSaveEdit}
                        normal
                        hoverEffect
                        backgroundColor="var(--color-blue)"
                        labelColor="var(--color-white)"
                    />
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={handleCloseDeleteDialog}
                maxWidth="sm"
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Delete sx={{ color: 'var(--color-red)' }} />
                    {t('ApproveFavoriteDelete')}
                </DialogTitle>
                <DialogContent>
                    <div style={{ marginTop: '8px' }}>
                        {t('FavoriteDelete')} <b>{deleteDialog.favorite?.Description}</b> ?
                    </div>
                </DialogContent>
                <DialogActions sx={{ padding: '16px' }}>
                    <M_Button
                        label={t('Cancel')}
                        onClick={handleCloseDeleteDialog}
                        normal
                        hoverEffect
                    />
                    <M_Button
                        label={t('Delete')}
                        onClick={handleDelete}
                        normal
                        hoverEffect
                        backgroundColor="var(--color-red)"
                        labelColor="var(--color-white)"
                    />
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default FavoritesSettings
